import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, Check, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Card, Empty, fmtTime } from "@/pages/portal/event-space/shared";

const TYPE_LABEL = { conference: "Conférence", round_table: "Table ronde", workshop: "Atelier", speed_meeting: "Networking / RDV 1:1", demo: "Démo", cocktail: "Cocktail", informal: "Networking libre" };

export default function Program({ space, slug }) {
  const [items, setItems] = useState([]);
  const [mine, setMine] = useState(new Set());
  const [type, setType] = useState("");
  const [period, setPeriod] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get(`/events/${space.event.id}/agenda`).then((r) => setItems((r.data || []).slice().sort((a, b) => (a.start_time || "").localeCompare(b.start_time || "")))).catch(() => {});
    if (space.state !== "not_registered") api.get(`/event-space/${slug}/agenda`).then((r) => setMine(new Set((r.data.sessions || []).map((s) => s.id)))).catch(() => {});
  }, [slug, space.event.id, space.state]);

  const types = useMemo(() => [...new Set(items.map((i) => i.animation_type))], [items]);
  const list = items.filter((i) => (!type || i.animation_type === type)
    && (!period || (period === "am" ? new Date(i.start_time).getHours() < 13 : new Date(i.start_time).getHours() >= 13))
    && (!q || `${i.title} ${i.description} ${i.location}`.toLowerCase().includes(q.toLowerCase())));

  const toggle = async (id) => {
    try {
      const r = await api.post(`/event-space/${slug}/sessions/${id}`);
      setMine((m) => { const n = new Set(m); r.data.in_agenda ? n.add(id) : n.delete(id); return n; });
      toast.success(r.data.in_agenda ? "Session ajoutée à mon agenda" : "Session retirée de mon agenda");
    } catch { toast.error("Action impossible"); }
  };

  return (
    <Card testId="program-section">
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-neutral-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une session" className="rounded-full border border-neutral-200 py-2 pl-8 pr-3 text-sm" data-testid="program-search" /></div>
        <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-full border border-neutral-200 px-3 py-2 text-sm" data-testid="program-filter-type"><option value="">Tous les formats</option>{types.map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}</select>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-full border border-neutral-200 px-3 py-2 text-sm" data-testid="program-filter-period"><option value="">Toute la journée</option><option value="am">Matin</option><option value="pm">Après-midi</option></select>
      </div>
      {list.length === 0 ? <Empty testId="program-empty">Aucune session ne correspond.</Empty> : (
        <ol className="relative space-y-3 border-l-2 border-wine-100 pl-5">
          {list.map((s) => (
            <li key={s.id} className="relative" data-testid={`program-item-${s.id}`}>
              <span className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-wine-500" />
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-neutral-100 p-3 hover:border-wine-100">
                <div className="min-w-0">
                  <div className="text-xs font-bold text-wine-600">{fmtTime(s.start_time)} – {fmtTime(s.end_time)} · {TYPE_LABEL[s.animation_type] || s.animation_type}</div>
                  <div className="text-sm font-bold text-navy">{s.title}</div>
                  {s.description && <p className="mt-0.5 text-xs text-neutral-600">{s.description}</p>}
                  <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-neutral-500">
                    {s.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.location}</span>}
                    {(s.speakers || []).length > 0 && <span>Intervenants : {s.speakers.map((x) => (typeof x === "string" ? x : x.name)).join(", ")}</span>}
                    {s.updated_at && <span className="rounded-full bg-amber-100 px-2 font-bold text-amber-800" data-testid={`program-updated-${s.id}`}>Modifié le {new Date(s.updated_at).toLocaleDateString("fr-FR")}</span>}
                  </div>
                </div>
                {space.state !== "not_registered" && s.animation_type !== "speed_meeting" && (
                  <button onClick={() => toggle(s.id)} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-bold ${mine.has(s.id) ? "bg-emerald-100 text-emerald-800" : "border border-wine-200 text-wine-700 hover:bg-wine-50"}`} data-testid={`program-toggle-${s.id}`}>
                    {mine.has(s.id) ? <><Check className="h-3.5 w-3.5" />Dans mon agenda</> : <><Plus className="h-3.5 w-3.5" />Ajouter à mon agenda</>}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}
